import { useEffect, useMemo } from "react";
import { useParticipants, useRoomContext } from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/shared/uikit/ToastV2/ToastProvider";
import { PARTICIPANT_ROLES } from "@circle-react/helpers/liveStreamRoomHelpers";
import type { ProviderRoom, ProviderRoomOptions } from "../types";
import { useRoom } from "../useRoom";
import { useLivekitErrorHandler } from "./useLivekitErrorHandler";
import { useLivekitLocalMetadata } from "./useLivekitLocalMetadata";

export const useLivekitRoom = ({
  token,
  onJoinError,
  isStreamEnded,
  participantData,
}: ProviderRoomOptions): ProviderRoom => {
  const circleRoom = useRoom();
  const url = circleRoom?.provider_url ?? "";
  const room = useRoomContext();
  const participants = useParticipants();
  const { metadata: localMetadata } = useLivekitLocalMetadata();
  const { error } = useToast();
  const isHost = localMetadata?.role === PARTICIPANT_ROLES.HOST;
  const isHlsSpectator =
    localMetadata?.role === PARTICIPANT_ROLES.HLS_SPECTATOR;

  useLivekitErrorHandler({
    hasDevicesAccess: !participantData?.isRecorder && isHost,
    onDisconnect: onJoinError,
  });

  useEffect(() => {
    const join = async () => {
      if (
        !!token &&
        !isStreamEnded &&
        room.state === ConnectionState.Disconnected
      ) {
        try {
          await room.connect(url, token, {
            autoSubscribe: !isHlsSpectator,
          });
        } catch (e: unknown) {
          if (e instanceof Error) {
            const errorMessage = `Could not connect to room: ${e.message}`;
            console.error(errorMessage);
            error(t("live_streams.room.join_error"));
            onJoinError && onJoinError(errorMessage);
          }
        }
      }
    };
    void join();

    return () => {
      if (room.state === ConnectionState.Connected) {
        void room.disconnect();
      }
    };
  }, [token, room, isStreamEnded]);

  const isSomeoneScreenSharing = useMemo(
    () => participants.some(participant => participant.isScreenShareEnabled),
    [participants],
  );

  return {
    isConnected: room.state === ConnectionState.Connected,
    isJoining: room.state === ConnectionState.Connecting,
    isHost,
    isHlsSpectator,
    role: localMetadata?.role,
    isSomeoneScreenSharing,
  };
};
